import { Alert, Divider, Radio, Switch } from 'antd';
import { FC, useEffect, useState } from 'react';
import { FaPlay, FaRegFolder } from 'react-icons/fa';
import { RxDashboard } from 'react-icons/rx';
import { useNavigate } from 'react-router';
import { XmButton } from 'src/component/button/XmButton';
import { XmInputNumberUnit } from 'src/component/compactAddon/XmInputNumberUnit';
import { XmListy } from 'src/component/list/XmListy';
import { XmModal } from 'src/component/modal/XmModal';
import { XmSpinner } from 'src/component/spinner/XmSpinner';
import { RoutePath } from 'src/data/Routes';
import { Log } from 'src/service/log.service';
import { GLOB } from 'src/util/Glob';
import {
  AUTOPLAY_INTERVAL_MAX_S, AUTOPLAY_INTERVAL_MIN_S, AUTOPLAY_MIN_DASHBOARDS, AUTOPLAY_PRESETS_S,
  formatAutoplayInterval, normalizeAutoplayInterval
} from './autoplayConst';
import { AutoplayQueueItem, loadFolderQueue } from './autoplayQueue';
import { closeAutoplayModal, enterTvMode, startAutoplay, useAutoplayStore } from './autoplayStore';
import './DashboardAutoplay.less';

const CUSTOM = 'custom';

type IntervalChoice = number | typeof CUSTOM;

const PRESET_OPTIONS: { label: string; value: IntervalChoice; }[] = [
  ...AUTOPLAY_PRESETS_S.map(seconds => ({ label: formatAutoplayInterval(seconds), value: seconds })),
  { label: 'Custom', value: CUSTOM },
];

const APPLICATION_SETTINGS_PATH = RoutePath.SETTINGS + RoutePath.CONFIGURATION + RoutePath.SET_BACKEND
  + '#tab=options&subTab=dashboard';

/**
 * Picks the interval and confirms the queue before playback starts. The queue is loaded here so
 * the user sees exactly what will play, in the order it will play.
 */
export const AutoplayStartModal: FC = () => {
  const navigate = useNavigate();
  const target = useAutoplayStore(s => s.target);
  const [queue, setQueue] = useState<AutoplayQueueItem[]>(undefined);
  const [missing, setMissing] = useState(false);
  const [choice, setChoice] = useState<IntervalChoice>(AUTOPLAY_PRESETS_S[0]);
  const [customS, setCustomS] = useState<number>(GLOB.dashboardAutoplayInterval);
  const [tv, setTv] = useState(false);

  useEffect(() => {
    // reset on close too: TV mode is never carried over from the previous session
    setTv(false);
    if (!target) return;

    const configured = normalizeAutoplayInterval(GLOB.dashboardAutoplayInterval);
    setChoice((AUTOPLAY_PRESETS_S as readonly number[]).includes(configured) ? configured : CUSTOM);
    setCustomS(configured);
    setQueue(undefined);
    setMissing(false);

    const aborter = new AbortController();
    loadFolderQueue(target.folderId, target.folderKind, aborter.signal).then(items => {
      if (aborter.signal.aborted) return;
      if (!items) {
        setMissing(true);
        setQueue([]);
        return;
      }
      setQueue(items);
    }, (reason: Error) => {
      if (aborter.signal.aborted) return;
      Log.error('Failed to list dashboards of the folder!', reason);
      closeAutoplayModal();
    });

    return () => aborter.abort();
  }, [target]);

  if (!target) return null;

  const intervalS = choice === CUSTOM ? customS : choice;
  const intervalValid = Number.isFinite(intervalS)
    && intervalS >= AUTOPLAY_INTERVAL_MIN_S && intervalS <= AUTOPLAY_INTERVAL_MAX_S;
  const loading = queue === undefined;
  const enough = !loading && queue.length >= AUTOPLAY_MIN_DASHBOARDS;
  const canStart = !loading && enough && intervalValid;

  const blockedReason = missing
    ? `Folder "${target.folderLabel}" no longer exists. Refresh the dashboard menu and try again.`
    : !loading && !enough
      ? `Folder "${target.folderLabel}" contains ${queue.length === 1 ? 'only one dashboard' : 'no dashboards'}`
      + `. Autoplay needs at least ${AUTOPLAY_MIN_DASHBOARDS} dashboards directly in the folder.`
      : null;

  // fullscreen is requested straight from the click, while the browser still counts it as a gesture
  const start = () => {
    if (!canStart) return;
    startAutoplay(queue, target.folderLabel, normalizeAutoplayInterval(intervalS));
    if (tv) void enterTvMode();
  };

  const openApplicationSettings = () => {
    closeAutoplayModal();
    navigate(APPLICATION_SETTINGS_PATH);
  };

  return (
    <XmModal
      open
      title={<span className="xm-autoplay-modal-title">
        Play dashboards
        <span className="xm-autoplay-modal-title-folder">
          <FaRegFolder />
          <span className="xm-autoplay-modal-title-label">{target.folderLabel}</span>
          {!loading && <>&middot; {queue.length} dashboard{queue.length === 1 ? '' : 's'}</>}
        </span>
      </span>}
      okText="Start autoplay"
      okButtonProps={{ disabled: !canStart, icon: <FaPlay /> }}
      onOk={start}
      onCancel={closeAutoplayModal}
      destroyOnHidden
      className="xm-autoplay-modal"
    >
      {loading
        ? <XmSpinner />
        : blockedReason
          ? <Alert type="warning" showIcon message={blockedReason} />
          : (
            <>
              <div className="xm-autoplay-modal-section">Switch dashboard every</div>
              <div className="xm-autoplay-modal-interval">
                <Radio.Group
                  options={PRESET_OPTIONS}
                  optionType="button"
                  value={choice}
                  onChange={e => setChoice(e.target.value as IntervalChoice)} />
                {choice === CUSTOM && (
                  <XmInputNumberUnit
                    autoFocus
                    unit="s"
                    min={AUTOPLAY_INTERVAL_MIN_S}
                    max={AUTOPLAY_INTERVAL_MAX_S}
                    step={5}
                    value={customS}
                    onChange={value => setCustomS(value as number)}
                    status={intervalValid ? undefined : 'error'} />
                )}
              </div>
              <div className={`xm-autoplay-modal-hint${intervalValid ? '' : ' xm-autoplay-modal-hint-error'}`}>
                {intervalValid
                  ? 'Time per dashboard, not data refresh.'
                  : `Interval must be between ${AUTOPLAY_INTERVAL_MIN_S} and ${AUTOPLAY_INTERVAL_MAX_S} seconds.`}
              </div>

              <div className="xm-autoplay-modal-section">Playback order</div>
              <XmListy<AutoplayQueueItem>
                size="small"
                viewportClassName="xm-autoplay-modal-list"
                items={queue}
                rowKey={item => item.id}
                itemRender={item => (
                  <div className="xm-autoplay-modal-list-row">
                    <RxDashboard />
                    <span className="xm-autoplay-modal-list-label">{item.label}</span>
                  </div>
                )} />
              <div className="xm-autoplay-modal-hint">
                Loops back to {queue[0].label} after {queue[queue.length - 1].label}.
              </div>

              <div className="xm-autoplay-modal-toggle">
                <div>
                  <div className="xm-autoplay-modal-section">TV mode</div>
                  <div className="xm-autoplay-modal-toggle-hint">Fill the screen while playing.</div>
                </div>
                <Switch checked={tv} onChange={setTv} aria-label="Start in TV mode" size="small" />
              </div>

              <Divider className="xm-autoplay-modal-divider" />

              <div className="xm-autoplay-modal-default">
                Default interval: {formatAutoplayInterval(normalizeAutoplayInterval(GLOB.dashboardAutoplayInterval))}
                {GLOB.userInfo?.isAdmin && <>
                  {' '}&middot;{' '}
                  <XmButton ignoreReadonly type="link" size="small" onClick={openApplicationSettings}>Application Settings</XmButton>
                </>}
              </div>
            </>
          )}
    </XmModal>
  );
};
